import { ColumnDef } from '@tanstack/react-table';

import { ProcessSpecificationDTO } from 'support/types';
import { WebEDIRowData } from 'support/types/webediTable';

import { TemplateColumn } from './hooks/useDownloadTemplate';

const LINE_ITEMS_PATH_PREFIX = 'line_items.*.';

/**
 * The line-items UI config the Portal renders (`useWebEDIStore.uiConfig`) is already resolved:
 * datachecks' `ui.view()` evaluates every CONDITIONAL field's modality against the *current*
 * document before generating it, collapsing each one to a final MANDATORY/OPTIONAL depending on
 * whether its condition currently matches. That's correct for the live table, but it means a
 * column's `meta.requirement` can never be `'conditional'` — the information is gone by the time
 * columns are generated.
 *
 * The downloaded template isn't tied to any one document, so it needs to flag fields whose
 * requiredness *depends* on the receiver's data regardless of how they currently resolve. That
 * information still exists on the raw, unresolved process specification fields (`processSpec.fields`
 * — never run through `ui.view()`), so we cross-reference by path and override the resolved
 * `requirement` for exactly those columns.
 */
export const markConditionalTemplateColumns = (
  columns: Array<TemplateColumn>,
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined,
): Array<TemplateColumn> => {
  const conditionalFieldIds = getConditionalLineItemFieldIds(processSpecFields);

  if (conditionalFieldIds.size === 0) {
    return columns;
  }

  return columns.map((column) => markColumn(column, conditionalFieldIds) as TemplateColumn);
};

/**
 * Line-item fields are addressed as `line_items.*.<relative path>` in the raw spec, but as just
 * `<relative path>` once datachecks relativizes them into the line-items section — which is also
 * how this modal's column ids are built. Strips that fixed prefix to get the matching column id.
 */
const getConditionalLineItemFieldIds = (
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined,
): ReadonlySet<string> => {
  const ids = new Set<string>();

  (processSpecFields ?? []).forEach((field) => {
    if (field.modality?.type === 'CONDITIONAL' && field.path.startsWith(LINE_ITEMS_PATH_PREFIX)) {
      ids.add(field.path.slice(LINE_ITEMS_PATH_PREFIX.length));
    }
  });

  return ids;
};

const markColumn = (
  column: ColumnDef<WebEDIRowData, unknown>,
  conditionalFieldIds: ReadonlySet<string>,
): ColumnDef<WebEDIRowData, unknown> => {
  const isConditional = typeof column.id === 'string' && conditionalFieldIds.has(column.id);
  // Only spread `column.meta` when it's defined — a column with no meta has no `type` to carry
  // over either, and `ColumnMeta.type` is required, so an unconditional spread of `undefined`
  // would produce a meta object TypeScript can no longer prove satisfies `ColumnMeta`.
  const meta = isConditional && column.meta ? { ...column.meta, requirement: 'conditional' as const } : column.meta;

  if ('columns' in column && Array.isArray(column.columns) && column.columns.length > 0) {
    return {
      ...column,
      meta,
      columns: column.columns.map((subColumn) => markColumn(subColumn, conditionalFieldIds)),
    };
  }

  return { ...column, meta };
};
